import { Component, Input, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DateStringAdapter } from '@components/ng-bootstrap-components.module';
import {
  NgbCalendar,
  NgbDate,
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbDateStruct,
  NgbInputDatepickerConfig,
} from '@ng-bootstrap/ng-bootstrap';
import { distinctUntilChanged, Subscription } from 'rxjs';

import { DatepickerBase } from './datepicker';

@Component({
  selector: 'app-daterange-picker',
  templateUrl: './daterange-picker.component.html',
  providers: [{ provide: NgbDateAdapter, useClass: DateStringAdapter }],
})
export class DaterangePickerComponent extends DatepickerBase implements OnInit, OnDestroy {
  @Input() disabled = false;
  @Input() displayMonths = 2;
  @Input() override placeholder = $localize`Wybierz zakres dat`;
  @Input() readonly fromDate: FormControl<string | null>;
  @Input() readonly toDate: FormControl<string | null>;
  ngbFromDate: NgbDate | null = null;
  ngbToDate: NgbDate | null = null;
  hoveredDate: NgbDate | null = null;
  private readonly subcriptions = new Subscription();

  constructor(
    private readonly adapter: NgbDateAdapter<string>,
    protected override readonly calendar: NgbCalendar,
    private readonly formatter: NgbDateParserFormatter,
    protected override readonly viewContainerRef: ViewContainerRef,
    protected override readonly ngbInputDatepickerConfig: NgbInputDatepickerConfig,
  ) {
    super(calendar, viewContainerRef, ngbInputDatepickerConfig);
  }

  get startDate(): NgbDateStruct | null {
    if (this.fromDate.value) {
      const d = new Date(this.fromDate.value);
      return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
    }
    return null;
  }

  get value(): string {
    return `${this.formatter.format(this.ngbFromDate) ?? ''}${
      this.formatter.format(this.ngbToDate) ? ` – ${this.formatter.format(this.ngbToDate)}` : ''
    }`;
  }

  ngOnInit(): void {
    const from = this.adapter.fromModel(this.fromDate?.value);
    const to = this.adapter.fromModel(this.toDate?.value);
    this.ngbFromDate = from ? new NgbDate(from.year, from.month, from.day) : null;
    this.ngbToDate = to ? new NgbDate(to.year, to.month, to.day) : null;

    this.subcriptions.add(
      this.fromDate?.valueChanges.pipe(distinctUntilChanged()).subscribe((fromDate) => {
        if (!fromDate) {
          this.ngbFromDate = null;
        }
      }),
    );
    this.subcriptions.add(
      this.toDate?.valueChanges.pipe(distinctUntilChanged()).subscribe((toDate) => {
        if (!toDate) {
          this.ngbToDate = null;
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subcriptions.unsubscribe();
  }

  onClosed(): void {
    this.fromDate.markAsTouched();
    this.toDate.markAsTouched();
  }

  onDateSelection(date: NgbDate): void {
    const setControl = (value: NgbDate, control: FormControl<string | null>): void => {
      control.markAsTouched();
      control.setValue(this.adapter.toModel(value));
      control.markAsDirty();
    };

    if (!this.ngbFromDate && !this.ngbToDate) {
      this.ngbFromDate = date;
      setControl(date, this.fromDate);
    } else if (this.ngbFromDate && !this.ngbToDate && date && date.after(this.ngbFromDate)) {
      this.ngbToDate = date;
      setControl(date, this.toDate);
      this.datepicker.close();
    } else {
      this.ngbToDate = null;
      this.ngbFromDate = date;
      setControl(date, this.fromDate);
    }
  }

  isHovered(date: NgbDate): boolean {
    return (
      this.ngbFromDate &&
      !this.ngbToDate &&
      this.hoveredDate &&
      date.after(this.ngbFromDate) &&
      date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate): boolean {
    return this.ngbToDate && date.after(this.ngbFromDate) && date.before(this.ngbToDate);
  }

  isRange(date: NgbDate): boolean {
    return (
      date.equals(this.ngbFromDate) ||
      (this.ngbToDate && date.equals(this.ngbToDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  isRangeStart(date: NgbDate): boolean {
    return this.ngbFromDate && this.ngbFromDate.equals(date);
  }

  isCurrentlyHovered(date: NgbDate): boolean {
    return (
      this.ngbFromDate &&
      date.after(this.ngbFromDate) &&
      this.hoveredDate &&
      date.equals(this.hoveredDate) &&
      !this.ngbToDate
    );
  }

  clear(): void {
    this.fromDate.reset(null);
    this.toDate.reset(null);
    this.ngbFromDate = null;
    this.ngbToDate = null;
  }
}
